import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { FloppyDisk, Clock, ChatText, Sliders, BellRinging } from "@phosphor-icons/react";

const WEEKDAY_LABELS = [
  { v: 0, l: "Seg" }, { v: 1, l: "Ter" }, { v: 2, l: "Qua" },
  { v: 3, l: "Qui" }, { v: 4, l: "Sex" }, { v: 5, l: "Sáb" }, { v: 6, l: "Dom" },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("triggers");

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        trigger_keywords: s.trigger_keywords,
        fuzzy_match: s.fuzzy_match,
        fuzzy_threshold: Number(s.fuzzy_threshold),
        limit_hours: Number(s.limit_hours),
        welcome_message: s.welcome_message,
        blocked_message: s.blocked_message,
        ask_name_message: s.ask_name_message,
        business_hours_enabled: s.business_hours_enabled,
        business_hours_start: s.business_hours_start,
        business_hours_end: s.business_hours_end,
        business_hours_weekdays: s.business_hours_weekdays,
        business_hours_timezone: s.business_hours_timezone,
        out_of_hours_message: s.out_of_hours_message,
      });
      setS(data);
      toast.success("Configurações salvas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <Layout title="Configurações"><div className="text-sm text-[#64748B]">Carregando…</div></Layout>;

  const setField = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const toggleWeekday = (v) => {
    const cur = new Set(s.business_hours_weekdays || []);
    cur.has(v) ? cur.delete(v) : cur.add(v);
    setS({ ...s, business_hours_weekdays: [...cur].sort() });
  };

  const tabs = [
    { id: "triggers", label: "Gatilhos & Templates", icon: Sliders },
    { id: "hours", label: "Horário de funcionamento", icon: Clock },
    { id: "messages", label: "Mensagens", icon: ChatText },
    { id: "followup", label: "Follow-up", icon: BellRinging },
  ];

  return (
    <Layout
      title="Configurações"
      subtitle="Bot / Mensagens / Limites / Horário"
      action={
        <button
          data-testid="settings-save-btn"
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 text-sm hover:bg-[#001f7a] transition-colors duration-150 disabled:opacity-50"
        >
          <FloppyDisk size={16} weight="bold" /> {saving ? "Salvando..." : "Salvar"}
        </button>
      }
    >
      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#E5E7EB]">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`settings-tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm transition-colors ${
              tab === id
                ? "border-b-2 border-[#002FA7] text-[#002FA7] font-medium"
                : "text-[#64748B] hover:text-[#0F172A]"
            }`}
          >
            <Icon size={16} weight={tab === id ? "bold" : "regular"} />
            {label}
          </button>
        ))}
      </div>

      {tab === "triggers" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-[#E5E7EB] bg-white p-6 lg:col-span-2">
            <div className="label-mono">Palavras-chave que disparam o bot</div>
            <h2 className="font-display font-bold text-lg mt-1 mb-3 tracking-tight">
              Triggers
            </h2>

            {/* Chips */}
            <div className="flex flex-wrap gap-2 mb-3">
              {(s.trigger_keywords || []).map((kw, i) => (
                <span
                  key={`${kw}-${i}`}
                  data-testid={`trigger-chip-${i}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#002FA7]/10 text-[#002FA7] text-xs"
                >
                  {kw}
                  <button
                    onClick={() => setS({
                      ...s,
                      trigger_keywords: s.trigger_keywords.filter((_, idx) => idx !== i),
                    })}
                    className="ml-1 hover:bg-[#002FA7]/20 px-1"
                    title="Remover"
                  >×</button>
                </span>
              ))}
              {(s.trigger_keywords || []).length === 0 && (
                <span className="text-xs text-[#64748B]">Sem triggers configurados. Adicione abaixo.</span>
              )}
            </div>

            {/* Add input */}
            <AddTriggerInput onAdd={(v) => {
              const exists = (s.trigger_keywords || []).some(
                (k) => k.toLowerCase().trim() === v.toLowerCase().trim()
              );
              if (exists) return;
              setS({ ...s, trigger_keywords: [...(s.trigger_keywords || []), v] });
            }} />

            {/* Match mode */}
            <div className="mt-5 pt-5 border-t border-[#E5E7EB]">
              <div className="label-mono mb-2">Como acionar o bot</div>
              <div className="space-y-2">
                {[
                  { v: "starts_with", l: "Começa com a palavra-chave", d: "Ex: 'teste joao' (recomendado)" },
                  { v: "exact", l: "Mensagem exata", d: "Cliente precisa mandar EXATAMENTE a palavra-chave" },
                  { v: "contains", l: "Contém a palavra-chave em qualquer lugar", d: "Ex: 'oi gostaria do teste por favor'" },
                ].map(({ v, l, d }) => (
                  <label key={v} className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                    (s.trigger_match_mode || "starts_with") === v
                      ? "border-[#002FA7] bg-[#002FA7]/5"
                      : "border-[#E5E7EB] hover:border-[#002FA7]"
                  }`}>
                    <input
                      type="radio"
                      name="match_mode"
                      checked={(s.trigger_match_mode || "starts_with") === v}
                      onChange={() => setS({ ...s, trigger_match_mode: v })}
                      data-testid={`trigger-mode-${v}`}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="text-sm font-medium">{l}</div>
                      <div className="text-xs text-[#64748B] mt-0.5">{d}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="border border-[#E5E7EB] bg-white p-6">
            <div className="label-mono">Match fuzzy (tolera erros de digitação)</div>
            <h2 className="font-display font-bold text-lg mt-1 mb-5 tracking-tight">
              Aceitar palavras parecidas
            </h2>
            <label className="flex items-center gap-3 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={!!s.fuzzy_match}
                onChange={(e) => setS({ ...s, fuzzy_match: e.target.checked })}
                data-testid="settings-fuzzy-toggle"
                className="h-4 w-4"
              />
              <span className="text-sm">Aceita "tste", "test grátis", "queroteste" como variações</span>
            </label>
            <label className="block text-xs text-[#64748B] mb-1">Sensibilidade (0.50 a 0.95)</label>
            <input
              type="number"
              step="0.05" min="0.5" max="0.95"
              value={s.fuzzy_threshold || 0.78}
              onChange={(e) => setS({ ...s, fuzzy_threshold: e.target.value })}
              data-testid="settings-fuzzy-threshold"
              disabled={!s.fuzzy_match}
              className="w-32 bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:bg-[#F8F9FA]"
            />
            <p className="text-xs text-[#64748B] mt-2 leading-relaxed">
              Quanto maior, mais rígido. Recomendado: 0.78.
            </p>
          </div>

          <div className="border border-[#E5E7EB] bg-white p-6">
            <div className="label-mono">Limite anti-abuso</div>
            <h2 className="font-display font-bold text-lg mt-1 mb-5 tracking-tight">
              Tempo entre testes por número
            </h2>
            <div className="flex items-center gap-3">
              <input
                data-testid="settings-limit-hours"
                type="number" min={0}
                value={s.limit_hours}
                onChange={setField("limit_hours")}
                className="w-32 bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
              <span className="text-sm text-[#64748B]">horas</span>
            </div>
            <p className="text-xs text-[#64748B] mt-2 leading-relaxed">
              O mesmo número (e IP) só recebe um novo teste após este intervalo.
            </p>
          </div>
        </div>
      )}

      {tab === "hours" && (
        <div className="border border-[#E5E7EB] bg-white p-6">
          <label className="flex items-center gap-3 cursor-pointer mb-6">
            <input
              type="checkbox"
              checked={!!s.business_hours_enabled}
              onChange={(e) => setS({ ...s, business_hours_enabled: e.target.checked })}
              data-testid="settings-hours-toggle"
              className="h-4 w-4"
            />
            <span className="text-sm font-medium">Ativar horário de funcionamento</span>
            <span className="text-xs text-[#64748B]">
              (fora do horário, bot envia a mensagem abaixo e não gera teste)
            </span>
          </label>

          <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${!s.business_hours_enabled ? "opacity-50 pointer-events-none" : ""}`}>
            <div>
              <label className="label-mono block mb-2">Início</label>
              <input
                type="time"
                value={s.business_hours_start || "08:00"}
                onChange={setField("business_hours_start")}
                data-testid="settings-hours-start"
                className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
            </div>
            <div>
              <label className="label-mono block mb-2">Fim</label>
              <input
                type="time"
                value={s.business_hours_end || "22:00"}
                onChange={setField("business_hours_end")}
                data-testid="settings-hours-end"
                className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
            </div>
            <div>
              <label className="label-mono block mb-2">Timezone</label>
              <input
                type="text"
                value={s.business_hours_timezone || "America/Sao_Paulo"}
                onChange={setField("business_hours_timezone")}
                data-testid="settings-hours-tz"
                className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono"
              />
            </div>
          </div>

          <div className={`mt-6 ${!s.business_hours_enabled ? "opacity-50 pointer-events-none" : ""}`}>
            <label className="label-mono block mb-3">Dias da semana</label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAY_LABELS.map(({ v, l }) => {
                const active = (s.business_hours_weekdays || []).includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleWeekday(v)}
                    data-testid={`settings-weekday-${v}`}
                    className={`px-4 py-2 text-sm transition-colors ${
                      active
                        ? "bg-[#002FA7] text-white"
                        : "bg-white border border-[#E5E7EB] text-[#0F172A] hover:border-[#002FA7]"
                    }`}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`mt-6 ${!s.business_hours_enabled ? "opacity-50 pointer-events-none" : ""}`}>
            <label className="label-mono block mb-2">Mensagem fora do horário</label>
            <textarea
              rows={4}
              value={s.out_of_hours_message || ""}
              onChange={setField("out_of_hours_message")}
              data-testid="settings-out-of-hours-msg"
              className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
            <p className="text-xs text-[#64748B] mt-2 leading-relaxed">
              Variáveis disponíveis: <span className="kbd">{"{inicio}"}</span> <span className="kbd">{"{fim}"}</span> <span className="kbd">{"{dias}"}</span>
            </p>
          </div>
        </div>
      )}

      {tab === "messages" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-[#E5E7EB] bg-white p-6 lg:col-span-2">
            <div className="label-mono">Mensagem de boas-vindas (teste criado)</div>
            <h2 className="font-display font-bold text-lg mt-1 mb-3 tracking-tight">
              Resposta quando o teste é gerado
            </h2>
            <p className="text-xs text-[#64748B] mb-3">
              Variáveis: <span className="kbd">{"{nome}"}</span> <span className="kbd">{"{usuario}"}</span> <span className="kbd">{"{senha}"}</span>
            </p>
            <textarea
              data-testid="settings-welcome-msg"
              rows={6}
              value={s.welcome_message || ""}
              onChange={setField("welcome_message")}
              className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </div>

          <Field
            testId="settings-ask-name"
            label="Quando o nome está faltando"
            value={s.ask_name_message}
            onChange={setField("ask_name_message")}
          />
          <Field
            testId="settings-blocked-msg"
            label="Quando está bloqueado / dentro do rate limit"
            value={s.blocked_message}
            onChange={setField("blocked_message")}
          />

          {/* Fallback */}
          <div className="border border-[#E5E7EB] bg-white p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-mono">Mensagem de fallback (cliente fala algo que NÃO bate trigger)</div>
                <h2 className="font-display font-bold text-lg mt-1 tracking-tight">
                  Bot responde mesmo quando não é "teste"?
                </h2>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!s.fallback_enabled}
                  onChange={(e) => setS({ ...s, fallback_enabled: e.target.checked })}
                  data-testid="settings-fallback-toggle"
                  className="h-4 w-4"
                />
                <span className="text-sm">Ativar</span>
              </label>
            </div>
            <textarea
              data-testid="settings-fallback-msg"
              rows={4}
              value={s.fallback_message || ""}
              onChange={setField("fallback_message")}
              disabled={!s.fallback_enabled}
              className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:bg-[#F8F9FA]"
            />
            <div className="mt-3 flex items-center gap-3">
              <label className="text-xs text-[#64748B]">Cooldown (min) — evita spam:</label>
              <input
                type="number" min={0}
                value={s.fallback_cooldown_minutes ?? 60}
                onChange={setField("fallback_cooldown_minutes")}
                data-testid="settings-fallback-cooldown"
                disabled={!s.fallback_enabled}
                className="w-24 bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:bg-[#F8F9FA]"
              />
              <span className="text-xs text-[#64748B]">
                O bot só envia o fallback uma vez a cada X min por número.
              </span>
            </div>
          </div>
        </div>
      )}

      {tab === "followup" && (
        <div className="border border-[#E5E7EB] bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono">Mensagem de follow-up automática</div>
              <h2 className="font-display font-bold text-lg mt-1 tracking-tight">
                Enviar mensagem X horas após criar o teste
              </h2>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!s.followup_enabled}
                onChange={(e) => setS({ ...s, followup_enabled: e.target.checked })}
                data-testid="settings-followup-toggle"
                className="h-4 w-4"
              />
              <span className="text-sm">Ativar follow-up</span>
            </label>
          </div>

          <p className="text-xs text-[#64748B] mb-5 leading-relaxed">
            Após o cliente receber o teste, o bot espera o tempo configurado e envia uma mensagem
            automática de follow-up — ideal pra perguntar se ele gostou ou oferecer um plano completo.
          </p>

          <div className={`space-y-4 ${!s.followup_enabled ? "opacity-50 pointer-events-none" : ""}`}>
            <div className="flex items-center gap-3">
              <label className="label-mono">Enviar após</label>
              <input
                type="number"
                step="0.5" min="0.1" max="48"
                value={s.followup_delay_hours ?? 1}
                onChange={setField("followup_delay_hours")}
                data-testid="settings-followup-delay"
                className="w-24 bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
              <span className="text-sm text-[#64748B]">horas (1 = uma hora, 0.5 = 30min)</span>
            </div>

            <div>
              <label className="label-mono block mb-2">Mensagem de follow-up</label>
              <textarea
                rows={6}
                value={s.followup_message || ""}
                onChange={setField("followup_message")}
                data-testid="settings-followup-msg"
                className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
              <p className="text-xs text-[#64748B] mt-2 leading-relaxed">
                Variáveis: <span className="kbd">{"{nome}"}</span> <span className="kbd">{"{usuario}"}</span> <span className="kbd">{"{senha}"}</span>
              </p>
            </div>

            <div className="border-l-2 border-[#FBBF24] bg-[#FEF3C7] p-3 text-xs text-[#92400E]">
              <strong>⚠️ Importante:</strong> O follow-up só é enviado para testes que foram entregues com sucesso ao
              cliente. Há um worker rodando a cada 5 minutos checando isso. Se você ativar agora, testes criados
              nas últimas 24h serão considerados se ainda não receberam follow-up.
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function AddTriggerInput({ onAdd }) {
  const [value, setValue] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    onAdd(v);
    setValue("");
  };
  return (
    <form onSubmit={submit} className="flex items-center gap-2">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid="trigger-add-input"
        placeholder='Ex: "teste grátis" ou "quero o teste"'
        className="flex-1 bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
      />
      <button
        type="submit"
        data-testid="trigger-add-btn"
        className="px-4 py-2 text-sm bg-[#002FA7] text-white hover:bg-[#001f7a]"
      >
        + Adicionar
      </button>
    </form>
  );
}


function Field({ testId, label, value, onChange }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-6">
      <label className="label-mono block mb-2">{label}</label>
      <textarea
        data-testid={testId}
        rows={5}
        value={value || ""}
        onChange={onChange}
        className="w-full bg-white border border-[#E5E7EB] px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
      />
    </div>
  );
}
